<template>
  <div>
    <h2 class="heading-2">
      {{ i18n(translations.footer_title) }}
    </h2>

    <p class="body-1 pb-24 pt-8">
      {{ i18n(translations.footer_instructions) }}
      <span v-if="lrrIsOngoing" data-test="lrr-text">{{
        i18n(translations.footer_late_response_rate)
      }}</span>
    </p>

    <RevButton
      size="small"
      :to="resolveLocalizedRoute({ name: DASHBOARD.MY_ORDERS.SELF })"
      variant="primary"
    >
      {{ i18n(translations.footer_instructions_cta) }}
    </RevButton>

    <FormattedMessage
      v-if="isIntercomLoadComplete"
      class="body-1 pb-44 pt-24"
      :definition="translations.footer_need_help_cta"
      tag="p"
    >
      <template #cta>
        <RevLink
          data-qa="messenger-cta"
          data-test="messenger-cta"
          @click="handleLinkClick"
        >
          {{ i18n(translations.footer_need_help_cta_link) }}
        </RevLink>
      </template>
    </FormattedMessage>

    <RevInfoBlock
      v-if="status === 'error' || isRetrying"
      class="mt-12"
      :content="i18n(translations.intercomInfoBlockErrorContent)"
      :icon="IconWarning"
      :title="i18n(translations.intercomInfoBlockErrorTitle)"
      variant="danger"
    >
      <RevButton
        size="small"
        variant="primary"
        :loading="isRetrying"
        :disabled="isRetrying"
        data-test="intercom-retry-cta"
        @click="handleRetryLoadingIntercom"
      >
        {{ i18n(translations.intercomInfoBlockErrorCta) }}
      </RevButton>
    </RevInfoBlock>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useIntercom } from '@backmarket/nuxt-layer-care-platform/intercom/useIntercom'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { IconWarning } from '@ds/icons/IconWarning'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { useCheckForLrr } from '~/scopes/care-commons/composables/useCheckForLrr'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'

import translations from './FooterBlock.translations'

const { DASHBOARD } = ROUTE_NAMES

const i18n = useI18n()

const { lrrIsOngoing } = useCheckForLrr()

const resolveLocalizedRoute = useRouteLocationWithLocale()

const { intercom, status, load, remove } = useIntercom()

const isRetrying = ref(false)

const isIntercomLoadComplete = computed(() => status.value === 'loaded')

function handleLinkClick() {
  intercom('show')
}

async function handleRetryLoadingIntercom() {
  isRetrying.value = true

  try {
    remove()
    await load()
  } finally {
    isRetrying.value = false
  }
}
</script>
